import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { AgCharts } from "ag-charts-react";
import Text from "../../../common/typography/text.js";
import { formatCurrency } from "../aabiFormatters";
import { formatCompactCurrency } from "../aabiViewModel";
import CompactCurrency from "./CompactCurrency";
import {
  getAabiChartBaseOptions,
  prefersReducedMotion,
  AABI_NUMBER_BIG_LABEL,
} from "../aabiChartOptions";
import "./../AABI.css";

// The presentational half of Receivables (§5.5), extracted so the manufacturer
// page (two slices: Past Due, Not Yet Due) and the Executive Dashboard (five
// aging buckets) draw the same donut and legend rows.
//
//   slices       [{ key, label, value, color }] in ring order
//   total        the centre figure and the denominator for slice shares
//   centerLabel  caption under the centre figure
//   rows         [{ key, label, value, color, valueColor, meta, url, ariaLabel }]
//                A row with a `url` is ONE link (stretched over the row); its
//                figure is static text so there is no nested button.
//   footerRows   same shape, rendered under a hairline (Sent to Collection)
//   summaryLabel accessible description of the chart
const ReceivablesDonut = ({
  slices,
  total,
  centerLabel = "Open Balance",
  rows = [],
  footerRows = [],
  summaryLabel,
  emptyMessage = "No open balance.",
  chartHeight = 180,
}) => {
  const hasBalance = total > 0;

  const chartOptions = useMemo(() => {
    if (!hasBalance) return null;
    const base = getAabiChartBaseOptions({
      reducedMotion: prefersReducedMotion(),
    });
    return {
      ...base,
      padding: { top: 8, right: 8, bottom: 8, left: 8 },
      data: slices.map((slice) => ({ bucket: slice.label, value: slice.value })),
      series: [
        {
          type: "donut",
          angleKey: "value",
          legendItemKey: "bucket",
          calloutLabel: { enabled: false },
          sectorLabel: { enabled: false },
          innerRadiusRatio: 0.66,
          innerLabels: [
            {
              text: formatCompactCurrency(total),
              ...AABI_NUMBER_BIG_LABEL,
            },
            { text: centerLabel, fontSize: 10 },
          ],
          fills: slices.map((slice) => slice.color),
          strokes: ["#FFFFFF"],
          strokeWidth: 2,
          tooltip: {
            // v11 result shape: { heading, title, data: [{ label, value }] }.
            renderer: ({ datum }) => ({
              title: datum.bucket,
              data: [
                { label: "Amount", value: formatCurrency(datum.value) },
                {
                  label: `Share of ${centerLabel.toLowerCase()}`,
                  value: `${((datum.value / total) * 100).toFixed(1)}%`,
                },
              ],
            }),
          },
        },
      ],
      legend: { enabled: false },
    };
  }, [hasBalance, slices, total, centerLabel]);

  const renderRow = (row) => {
    const linked = Boolean(row.url);
    return (
      <div
        className={`aabi-receivables-row${linked ? " aabi-receivables-row-linked" : ""}`}
        key={row.key ?? row.label}
      >
        <dt>
          <span className="aabi-receivables-label">
            <span
              className="aabi-receivables-swatch"
              style={{ backgroundColor: row.color }}
              aria-hidden="true"
            />
            {linked ? (
              <Link
                to={row.url}
                className="aabi-receivables-row-link"
                aria-label={row.ariaLabel}
              >
                <Text variant="smallText" color="lightText">
                  {row.label}
                </Text>
              </Link>
            ) : (
              <Text variant="smallText" color="lightText">
                {row.label}
              </Text>
            )}
          </span>
        </dt>
        <dd>
          <CompactCurrency
            value={row.value}
            variant="smallText"
            color={row.valueColor ?? "darkText"}
            className="aabi-receivables-value"
            staticValue={linked}
          />
          {row.meta && (
            <Text variant="tinyText" color="lightText" className="aabi-receivables-meta">
              {row.meta}
            </Text>
          )}
        </dd>
      </div>
    );
  };

  return (
    <div className="aabi-receivables-body">
      {hasBalance ? (
        <div className="aabi-receivables-chart" role="img" aria-label={summaryLabel}>
          <AgCharts options={chartOptions} style={{ width: "100%", height: chartHeight }} />
        </div>
      ) : (
        <div className="aabi-empty-state aabi-empty-state-compact">
          <Text variant="smallText" color="lightText">
            {emptyMessage}
          </Text>
        </div>
      )}

      <dl className="aabi-receivables-rows">{rows.map(renderRow)}</dl>

      {footerRows.length > 0 && (
        <dl className="aabi-receivables-rows aabi-receivables-footer">
          {footerRows.map(renderRow)}
        </dl>
      )}
    </div>
  );
};

export default ReceivablesDonut;
